"use strict";

/**
 * @classdesc
 * Implementierung des transaktionellen Feature-Service.
 *
 * @class
 * @extends gc.gis.service.AbstractFeatureService
 * @memberof gc.gis.service
 * @author Christian Schmidt <c.schmidt@brain-scc.de>
 *
 * @param {gc.gis.util.GCRequestManager} requestManager Objekt zur Ausführung von Requests
 * @param {string} addFeatureUrl Service-URL zum Anlegen neuer Objekte
 * @param {string} updateAppendUrl Service-URL zum Aktualisieren von Objekten
 * @param {string} deleteFeatureUrl Service-URL zum Löschen von Objekten
 */
gc.gis.service.TransactionFeatureService = function(requestManager, addFeatureUrl, updateAppendUrl, deleteFeatureUrl)
{
	gc.gis.service.AbstractFeatureService.call(this, requestManager);

	/**
	 * @type {string}
	 */
	this._addFeatureUrl     = addFeatureUrl;
	/**
	 * @type {string}
	 */
	this._updateAppendUrl   = updateAppendUrl;
	/**
	 * @type {string}
	 */
	this._deleteFeatureUrl  = deleteFeatureUrl;
	/**
	 * @type {gc.gis.format.GCFormat}
	 */
	this._geometryFormatter = gc.gis.iface.mapFactory.createFormat();
};
gc.gis.service.TransactionFeatureService.prototype = Object.create(gc.gis.service.AbstractFeatureService.prototype);

/**
 * Fügt Features einem Datenspeicher hinzu.
 *
 * @param {gc.gis.layer.GCLayer} layer
 * @param {gc.gis.service.feature.AbstractTransactionFeature[]} features
 */
gc.gis.service.TransactionFeatureService.prototype.addFeature = function(layer, features)
{
	var self = this;
	var url_params = {};
	var request = {};

	url_params.layer = layer.getName();

	request.url = gc.gis.GCUrl.buildUrl(this._addFeatureUrl, url_params);
	request.data = JSON.stringify(this._writeFeaturesObject(layer, features));
	request.contentType = gc.gis.util.GCRequestManagerContentType.JSON;
	request.method = 'POST';
	request.context = this;

	request.success = function(data)
	{
		// der Request wurde erfolgreich verarbeitet, d.h. nicht, dass alle Objekte erfolgreich gespeichert werden konnten
		self._fireEvent(gc.gis.service.FeatureServiceEvent.ADD_SUCCESS, {
			service: self,
			layer: layer,
			features: features,
			result: data.addResults
		});
	};
	request.error = function(err)
	{
		self._fireEvent(gc.gis.service.FeatureServiceEvent.ADD_ERROR, {
			service: self,
			layer: layer,
			features: features,
			error: err
		});
	};

	this._requestManager.ajax(request);
};

/**
 * Aktualisiert Features in einem Datenspeicher.
 *
 * @param {gc.gis.layer.GCLayer} layer
 * @param {gc.gis.service.feature.AbstractTransactionFeature[]} features
 */
gc.gis.service.TransactionFeatureService.prototype.updateFeature = function(layer, features)
{
	var self = this,
		url_params = {},
		request = {};

	url_params.layer = layer.getName();

	request.url = gc.gis.GCUrl.buildUrl(this._updateAppendUrl, url_params);
	request.data = JSON.stringify(this._writeFeaturesObject(layer, features));
	request.contentType = gc.gis.util.GCRequestManagerContentType.JSON;
	request.method = 'POST';
	request.context = this;

	request.success = function(data)
	{
		// der Request wurde erfolgreich verarbeitet, d.h. nicht, dass alle Objekte erfolgreich gespeichert werden konnten
		self._fireEvent(gc.gis.service.FeatureServiceEvent.UPDATE_SUCCESS, {
			service: self,
			layer: layer,
			features: features,
			result: data.updateResults
		});
	};
	request.error = function(err)
	{
		self._fireEvent(gc.gis.service.FeatureServiceEvent.UPDATE_ERROR, {
			service: self,
			layer: layer,
			features: features,
			error: err
		});
	};

	this._requestManager.ajax(request);
};

/**
 * Löscht Features in einem Datenspeicher.
 *
 * @param {gc.gis.layer.GCLayer} layer
 * @param {gc.gis.feature.GCFeature[]} features
 */
gc.gis.service.TransactionFeatureService.prototype.deleteFeature = function(layer, features)
{
	var self = this;
	var url_params = {};
	var request = {};

	url_params.layer = layer.getName();

	request.url = gc.gis.GCUrl.buildUrl(this._deleteFeatureUrl, url_params);
	request.data = JSON.stringify(this._writeFeaturesObject(layer, features));
	request.contentType = gc.gis.util.GCRequestManagerContentType.JSON;
	request.method = 'POST';
	request.context = this;

	request.success = function(data)
	{
		// der Request wurde erfolgreich verarbeitet, d.h. nicht, dass alle Objekte erfolgreich gelöscht werden konnten
		self._fireEvent(gc.gis.service.FeatureServiceEvent.DELETE_SUCCESS, {
			service: self,
			layer: layer,
			features: features,
			result: data.deleteResults
		});
	};
	request.error = function(err)
	{
		self._fireEvent(gc.gis.service.FeatureServiceEvent.DELETE_ERROR, {
			service: self,
			layer: layer,
			features: features,
			error: err
		});
	};

	this._requestManager.ajax(request);
};

/**
 * Kodiert eine Liste von Features für die Übertragung per Feature-Service.
 *
 * @private
 * @param {gc.gis.layer.GCLayer} layer
 * @param {gc.gis.service.feature.AbstractTransactionFeature[]} features
 */
gc.gis.service.TransactionFeatureService.prototype._writeFeaturesObject = function(layer, features)
{
	var object = ({
			features: []
		}),
		i, ii;

	for (i = 0, ii = features.length; i < ii; i++)
	{
		object.features.push(this._writeFeatureObject(layer, features[i]));
	}

	return object;
};

/**
 * Kodiert ein Feature für die Übertragung per Feature-Service.
 *
 * @private
 * @param {gc.gis.layer.GCLayer} layer
 * @param {gc.gis.service.feature.AbstractTransactionFeature} feature
 */
gc.gis.service.TransactionFeatureService.prototype._writeFeatureObject = function(layer, feature)
{
	var object = {},
		value, list, bezug,
		geometry,
		i, ii;


	// Typ
	if (feature instanceof gc.gis.service.feature.TransactionFachDBFeature)
	{
		value = gc.gis.service.FeatureServiceTypes.FEATURETYPE_FACHDB;
	}
	else
	{
		value = gc.gis.service.FeatureServiceTypes.FEATURETYPE_FEATURE;
	}
	object.type = value;

	// ID
	value = feature.getID();
	if (value)
	{
		object.featureId = value;
	}

	// Attribute
	object.properties = feature.getAttributes();

	// Spezialisierungen in Abhängigkeit des Typs
	if (feature instanceof gc.gis.service.feature.TransactionFachDBFeature)
	{
		list = feature.getGeometryList();
		if (list && list.length > 0)
		{
			for (i = 0, ii = list.length; i < ii; i++)
			{
				bezug = gc.gis.GCUtil.assign({}, list[i]);  // Eigenschaften kopieren, da diese abgeändert werden
				if (bezug.geometry)
				{
					object.geometryFormat = gc.gis.format.GEOJSON;
					object.geometryCRS = bezug.geometry.getCRS();   // CRS sollte bei allen Geometrien des Objekts gleich sein
					geometry = this._writeGeometryObject(bezug.geometry);
					delete bezug.geometry;
					bezug.geometry = geometry;
				}

				if (!object.fb_koordinaten)
				{
					object.fb_koordinaten = [];
				}

				object.fb_koordinaten.push(bezug);
			}
		}

		list = feature.getAddressList();
		if (list && list.length > 0)
		{
			object.fb_adressen = list;
		}

		list = feature.getLandparcelList();
		if (list && list.length > 0)
		{
			object.fb_flurstuecke = list;
		}

		list = feature.getMediaList();
		if (list && list.length > 0)
		{
			object.fb_media = list;
		}
	}
	else if (feature instanceof gc.gis.service.feature.TransactionSimpleFeature)
	{
		geometry = feature.getGeometry();
		if (geometry)
		{
			object.geometryFormat = gc.gis.format.GEOJSON;
			object.geometryCRS = geometry.getCRS();
			object.geometry = this._writeGeometryObject(geometry);
		}
	}

	return object;
};

/**
 * Kodiert ein Feature für die Übertragung per Feature-Service.
 *
 * @private
 * @param {gc.gis.geom.GCGeometry} geometry
 * @return {Object} GeoJSON kodierte Geometrie
 */
gc.gis.service.TransactionFeatureService.prototype._writeGeometryObject = function(geometry)
{
	if (geometry instanceof gc.gis.geom.GCCircle)
	{
		geometry = geometry.toPolygon();
	}

	return this._geometryFormatter.writeGeometryObject(geometry);
};